import { useQuery } from '@tanstack/react-query';
import { featureFlagsAPI, FeatureFlagCheck } from '../api/featureFlags';
import {
  defaultFeatureFlagCheck,
  defaultFeatureFlagEnabled,
} from '../constants/featureFlags';
import { useAuth } from './useAuth';

const FEATURE_FLAG_KEYS = {
  all: ['featureFlags'] as const,
  check: (flagName: string) =>
    [...FEATURE_FLAG_KEYS.all, 'check', flagName] as const,
  checkMultiple: (flagNames: string[]) =>
    [...FEATURE_FLAG_KEYS.all, 'checkMultiple', flagNames] as const,
};

export { defaultFeatureFlagEnabled } from '../constants/featureFlags';

/**
 * Hook to check if a specific feature flag is enabled for the current user
 * Returns a disabled flag if the flag doesn't exist or is deleted
 */
export const useFeatureFlag = (flagName: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: FEATURE_FLAG_KEYS.check(flagName),
    queryFn: () => featureFlagsAPI.check(flagName),
    enabled: isAuthenticated, // Only fetch if user is authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    placeholderData: defaultFeatureFlagCheck(flagName),
  });
};

/**
 * Hook to check multiple feature flags for the current user
 */
export const useFeatureFlags = (flagNames: string[]) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: FEATURE_FLAG_KEYS.checkMultiple(flagNames),
    queryFn: () => featureFlagsAPI.checkMultiple(flagNames),
    enabled: isAuthenticated && flagNames.length > 0, // Only fetch if user is authenticated and flags are provided
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });
};

/**
 * Helper function to check if a feature flag is enabled
 */
export const isFeatureEnabled = (
  flag: FeatureFlagCheck | undefined,
  flagName?: string
): boolean => {
  if (!flag) {
    return flagName ? defaultFeatureFlagEnabled(flagName) : false;
  }
  return flag.is_enabled;
};
